import { type ReactNode, type Ref, useEffect, useRef } from "react";

import { tv, type VariantProps } from "tailwind-variants";

import { cn } from "@virtual-brain-lib/frontend-design-system/utils";

import { PanelGroup } from "./PanelGroup";
import { PanelResizeHandle } from "./PanelResizeHandle";

export const BREAKPOINTS = ["base", "sm", "md", "lg", "xl", "2xl"] as const;
export type Breakpoint = (typeof BREAKPOINTS)[number];

const panelVariants = tv({
	slots: {
		base: "overflow-hidden bg-background transition-[flex-basis,translate] duration-500 ease-out motion-reduce:duration-0",
		sm: "",
		md: "",
		lg: "",
		xl: "",
		"2xl": "",
	},
	variants: {
		mode: {
			inflow: {
				base: "relative top-auto right-auto bottom-auto left-auto z-auto w-auto shrink-0 translate-x-0",
				sm: "sm:relative sm:top-auto sm:right-auto sm:bottom-auto sm:left-auto sm:z-auto sm:w-auto sm:shrink-0 sm:translate-x-0",
				md: "md:relative md:top-auto md:right-auto md:bottom-auto md:left-auto md:z-auto md:w-auto md:shrink-0 md:translate-x-0",
				lg: "lg:relative lg:top-auto lg:right-auto lg:bottom-auto lg:left-auto lg:z-auto lg:w-auto lg:shrink-0 lg:translate-x-0",
				xl: "xl:relative xl:top-auto xl:right-auto xl:bottom-auto xl:left-auto xl:z-auto xl:w-auto xl:shrink-0 xl:translate-x-0",
				"2xl": "2xl:relative 2xl:top-auto 2xl:right-auto 2xl:bottom-auto 2xl:left-auto 2xl:z-auto 2xl:w-auto 2xl:shrink-0 2xl:translate-x-0",
			},
			overlay: {
				base: "absolute top-0 bottom-0 z-10 shrink basis-auto",
				sm: "sm:absolute sm:top-0 sm:bottom-0 sm:z-10 sm:shrink sm:basis-auto",
				md: "md:absolute md:top-0 md:bottom-0 md:z-10 md:shrink md:basis-auto",
				lg: "lg:absolute lg:top-0 lg:bottom-0 lg:z-10 lg:shrink lg:basis-auto",
				xl: "xl:absolute xl:top-0 xl:bottom-0 xl:z-10 xl:shrink xl:basis-auto",
				"2xl": "2xl:absolute 2xl:top-0 2xl:bottom-0 2xl:z-10 2xl:shrink 2xl:basis-auto",
			},
		},
		side: { left: {}, right: {} },
		width: {
			full: {},
			"1/2": {},
			"1/3": {},
			"2/3": {},
		},
		isOpen: { true: {}, false: {} },
	},
	defaultVariants: { side: "right" },
	compoundVariants: [
		{
			mode: "overlay",
			side: "right",
			class: {
				base: "right-0 left-auto",
				sm: "sm:right-0 sm:left-auto",
				md: "md:right-0 md:left-auto",
				lg: "lg:right-0 lg:left-auto",
				xl: "xl:right-0 xl:left-auto",
				"2xl": "2xl:right-0 2xl:left-auto",
			},
		},
		{
			mode: "overlay",
			side: "left",
			class: {
				base: "right-auto left-0",
				sm: "sm:right-auto sm:left-0",
				md: "md:right-auto md:left-0",
				lg: "lg:right-auto lg:left-0",
				xl: "xl:right-auto xl:left-0",
				"2xl": "2xl:right-auto 2xl:left-0",
			},
		},

		{
			mode: "inflow",
			isOpen: true,
			width: "full",
			class: {
				base: "basis-full",
				sm: "sm:basis-full",
				md: "md:basis-full",
				lg: "lg:basis-full",
				xl: "xl:basis-full",
				"2xl": "2xl:basis-full",
			},
		},
		{
			mode: "inflow",
			isOpen: true,
			width: "1/2",
			class: {
				base: "basis-1/2",
				sm: "sm:basis-1/2",
				md: "md:basis-1/2",
				lg: "lg:basis-1/2",
				xl: "xl:basis-1/2",
				"2xl": "2xl:basis-1/2",
			},
		},
		{
			mode: "inflow",
			isOpen: true,
			width: "1/3",
			class: {
				base: "basis-1/3",
				sm: "sm:basis-1/3",
				md: "md:basis-1/3",
				lg: "lg:basis-1/3",
				xl: "xl:basis-1/3",
				"2xl": "2xl:basis-1/3",
			},
		},
		{
			mode: "inflow",
			isOpen: true,
			width: "2/3",
			class: {
				base: "basis-2/3",
				sm: "sm:basis-2/3",
				md: "md:basis-2/3",
				lg: "lg:basis-2/3",
				xl: "xl:basis-2/3",
				"2xl": "2xl:basis-2/3",
			},
		},

		{
			mode: "inflow",
			isOpen: false,
			class: {
				base: "basis-0",
				sm: "sm:basis-0",
				md: "md:basis-0",
				lg: "lg:basis-0",
				xl: "xl:basis-0",
				"2xl": "2xl:basis-0",
			},
		},

		{
			mode: "overlay",
			width: "full",
			class: {
				base: "w-full",
				sm: "sm:w-full",
				md: "md:w-full",
				lg: "lg:w-full",
				xl: "xl:w-full",
				"2xl": "2xl:w-full",
			},
		},
		{
			mode: "overlay",
			width: "1/2",
			class: {
				base: "w-1/2",
				sm: "sm:w-1/2",
				md: "md:w-1/2",
				lg: "lg:w-1/2",
				xl: "xl:w-1/2",
				"2xl": "2xl:w-1/2",
			},
		},
		{
			mode: "overlay",
			width: "1/3",
			class: {
				base: "w-1/3",
				sm: "sm:w-1/3",
				md: "md:w-1/3",
				lg: "lg:w-1/3",
				xl: "xl:w-1/3",
				"2xl": "2xl:w-1/3",
			},
		},
		{
			mode: "overlay",
			width: "2/3",
			class: {
				base: "w-2/3",
				sm: "sm:w-2/3",
				md: "md:w-2/3",
				lg: "lg:w-2/3",
				xl: "xl:w-2/3",
				"2xl": "2xl:w-2/3",
			},
		},

		{
			mode: "overlay",
			isOpen: true,
			class: {
				base: "translate-x-0",
				sm: "sm:translate-x-0",
				md: "md:translate-x-0",
				lg: "lg:translate-x-0",
				xl: "xl:translate-x-0",
				"2xl": "2xl:translate-x-0",
			},
		},
		{
			mode: "overlay",
			isOpen: false,
			side: "right",
			class: {
				base: "translate-x-full",
				sm: "sm:translate-x-full",
				md: "md:translate-x-full",
				lg: "lg:translate-x-full",
				xl: "xl:translate-x-full",
				"2xl": "2xl:translate-x-full",
			},
		},
		{
			mode: "overlay",
			isOpen: false,
			side: "left",
			class: {
				base: "-translate-x-full",
				sm: "sm:-translate-x-full",
				md: "md:-translate-x-full",
				lg: "lg:-translate-x-full",
				xl: "xl:-translate-x-full",
				"2xl": "2xl:-translate-x-full",
			},
		},
	],
});

type PanelVariants = NonNullable<VariantProps<typeof panelVariants>>;

export type PanelBreakpointConfig = PanelVariants & {
	readonly resizeMinWidth?: number;
	readonly resizeMaxWidth?: number;
};

export type PanelConfig = Partial<Record<Breakpoint, PanelBreakpointConfig>> & {
	readonly base: PanelBreakpointConfig;
};

function buildResponsiveClasses(config: PanelConfig, isOpen: boolean): string {
	return BREAKPOINTS.map((bp) => {
		const bpConfig = config[bp];

		if (!bpConfig) {
			return "";
		}

		const slots = panelVariants({
			mode: bpConfig.mode,
			width: bpConfig.width,
			side: bpConfig.mode === "overlay" ? (bpConfig.side ?? "right") : undefined,
			isOpen,
		});

		return slots[bp]();
	})
		.filter(Boolean)
		.join(" ");
}

function buildResizeConstraints(config: PanelConfig): string | undefined {
	const constraints: Record<string, { min?: number; max?: number }> = {};
	let hasConstraints = false;

	for (const bp of BREAKPOINTS) {
		const bpConfig = config[bp];

		if (!bpConfig) {
			continue;
		}

		if (bpConfig.resizeMinWidth !== undefined || bpConfig.resizeMaxWidth !== undefined) {
			constraints[bp] = {};

			if (bpConfig.resizeMinWidth !== undefined) {
				constraints[bp].min = bpConfig.resizeMinWidth;
			}

			if (bpConfig.resizeMaxWidth !== undefined) {
				constraints[bp].max = bpConfig.resizeMaxWidth;
			}

			hasConstraints = true;
		}
	}

	return hasConstraints ? JSON.stringify(constraints) : undefined;
}

type PanelProps = {
	readonly children: ReactNode;
	readonly isOpen?: boolean;
	readonly config?: PanelConfig;
	readonly hasTransition?: boolean;
	readonly className?: string;
	readonly ref?: Ref<HTMLDivElement>;
};

function resetGroupResizeStyles(panel: HTMLElement) {
	const group = panel.closest("[data-panel-group]");

	if (group) {
		Array.from(group.children).forEach((child) => {
			if (child instanceof HTMLElement) {
				child.style.flexBasis = "";
				child.style.flexGrow = "";
			}
		});
	}
}

function setRef<T>(ref: Ref<T> | undefined, value: T | null) {
	if (typeof ref === "function") {
		ref(value);
	} else if (ref) {
		(ref as { current: T | null }).current = value;
	}
}

function PanelBase({
	children,
	isOpen,
	config,
	hasTransition = true,
	className,
	ref: externalRef,
}: PanelProps) {
	const internalRef = useRef<HTMLDivElement>(null);

	const mergedRef = (node: HTMLDivElement | null) => {
		internalRef.current = node;
		setRef(externalRef, node);
	};

	useEffect(() => {
		if (isOpen === undefined || !internalRef.current) {
			return;
		}

		resetGroupResizeStyles(internalRef.current);
	}, [isOpen]);

	const resizeConstraints = config ? buildResizeConstraints(config) : undefined;

	if (isOpen === undefined) {
		return (
			<div
				ref={mergedRef}
				data-resize-constraints={resizeConstraints}
				className={cn("min-w-0 flex-1 overflow-hidden bg-background", className)}
			>
				{children}
			</div>
		);
	}

	const panelConfig = config ?? {
		base: { mode: "overlay" as const, width: "full" as const, side: "right" as const },
	};
	const responsiveClasses = buildResponsiveClasses(panelConfig, isOpen);

	return (
		<div
			ref={mergedRef}
			data-resize-constraints={resizeConstraints}
			className={cn(responsiveClasses, !hasTransition && "transition-none", className)}
		>
			{children}
		</div>
	);
}

export const Panel = Object.assign(PanelBase, {
	Group: PanelGroup,
	ResizeHandle: PanelResizeHandle,
});
